import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Eye, EyeOff, Mail } from 'lucide-react';

// Veecheck Logo URL
// Logo URLs
const NASSCOM_LOGO = "https://customer-assets.emergentagent.com/job_d55b4566-cfe4-4d55-bff5-ea518d6087bb/artifacts/9gl9h3eb_NASSCOM.jpg";
const ISO_LOGO = "https://customer-assets.emergentagent.com/job_d55b4566-cfe4-4d55-bff5-ea518d6087bb/artifacts/fb4pvqf5_ISO%20Image.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_veecheck-staging/artifacts/w5naci8s_Veecheck%20logo.png";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Force password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/auth/login', { email, password });
      
      // Check if user needs to change password
      if (response.data.user?.force_password_change) {
        setPendingToken(response.data.token);
        setPendingUser(response.data.user);
        setShowChangePassword(true);
        toast.info('Please change your password to continue');
      } else {
        toast.success('Login successful!');
        onLogin(response.data.token, response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      // Temporarily set auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${pendingToken}`;
      
      await axios.post('/auth/change-password', {
        new_password: newPassword
      });
      
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      
      // Now complete login with updated user (remove force_password_change flag)
      const updatedUser = { ...pendingUser, force_password_change: false };
      onLogin(pendingToken, updatedUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
      // Remove temp auth header on error
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setChangingPassword(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSendingReset(true);
    try {
      const response = await axios.post('/auth/forgot-password', { email: forgotEmail.trim() });
      toast.success('Password reset instructions sent to your email');
      
      // For testing, show the temporary password if returned
      if (response.data.temp_password_for_testing) {
        toast.info(`Temporary password: ${response.data.temp_password_for_testing}`, { duration: 10000 });
      }
      
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-slate-50 to-slate-100 relative"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-12">
            <img 
              src={LOGO_URL} 
              alt="Veecheck Logo" 
              className="w-80 h-auto mx-auto mb-8"
            />
            <p className="text-xl text-slate-600 font-medium">Trust But Verify First</p>
            <p className="text-base text-slate-500 mt-2">Background Verification Services</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="space-y-2">
            <div className="flex items-center justify-center gap-6 mb-4">
              <img 
                src={NASSCOM_LOGO} 
                alt="NASSCOM Member" 
                className="h-14 w-auto object-contain"
              />
              <img 
                src={ISO_LOGO} 
                alt="ISO 27001" 
                className="h-14 w-auto object-contain"
              />
            </div>
            <CardTitle className="text-2xl text-center">
              <span className="text-red-600">Vee</span>
              <span className="text-yellow-500">check</span>
            </CardTitle>
            <CardDescription className="text-base text-center">Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="border-slate-200 focus:ring-green-600"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="border-slate-200 focus:ring-green-600 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-red-600 via-yellow-500 to-green-600 hover:from-red-700 hover:via-yellow-600 hover:to-green-700"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              
              {/* Forgot Password Link */}
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-500" />
              Change Your Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium">Password Change Required</p>
              <p className="mt-1">For security, please set a new password to continue.</p>
            </div>
            
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !newPassword || newPassword !== confirmPassword}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {changingPassword ? 'Changing...' : 'Change Password & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="forgot-email">Email Address</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="Enter your registered email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="mt-1"
                autoFocus
                data-testid="forgot-password-email"
              />
              <p className="text-xs text-slate-500 mt-2">
                We'll send a temporary password to this email address.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sendingReset || !forgotEmail.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="forgot-password-submit"
              >
                {sendingReset ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
